import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DetectMethodChanges, DetectSetterChanges } from '../utils';
import { OrionSettingsService } from '../orion-settings-dialog/orion-settings.service';
import { OrionSettingsDto } from '../shell-communication/shell-operations-protocol';
import { WeekdayExclusionBox, weekdayExclusionBoxes } from '../orion-settings-dialog/orion-settings-dialog.component';

// CR-20260910-001 (2026-09-14, owner ruling): Maven's settings are NOT ORION settings. Maven is a
// consumer of the ORIONs, not one of them, so it gets its own host-menu item ("MAVEN Settings")
// and its own dialog. Super-user-only on the surface (the menu item is hidden from everyone else)
// and on the wire (the Shell refuses a non-super-user save).
//
// TWO SETTINGS (owner, 2026-09-14): "Show top N results" - how many result cards Maven draws -
// and "Max Query Range (days)" - the widest date range a Maven query may span. Both are Maven's
// own values; until this date it borrowed the APG ORION's. The nearby-reports window and the run
// budget stay on the wire at their defaults and are NOT editable here.
//
// STORAGE IS SHARED. Both dialogs read and write the one shell-level settings record through
// OrionSettingsService; this dialog patches ONLY its own field and carries every other field
// through as held, so a MAVEN save never overwrites an APG cap and vice versa.
@Component({
   selector: 'ets-maven-settings-dialog',
   templateUrl: './maven-settings-dialog.component.html',
   styleUrls: ['./maven-settings-dialog.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class MavenSettingsDialogComponent {
   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _orionSettingsService: OrionSettingsService,
      private readonly _toastr: ToastrService
   ) { }

   title = 'MAVEN Settings';

   private _isVisible = false;
   get isVisible(): boolean {
      return this._isVisible;
   }
   @DetectSetterChanges()
   set isVisible(v: boolean) {
      this._isVisible = v;
   }

   private _isLoading = false;
   get isLoading(): boolean {
      return this._isLoading;
   }
   @DetectSetterChanges()
   set isLoading(v: boolean) {
      this._isLoading = v;
   }

   // The editable copy. Re-read from the service every time the dialog opens, so a Close without
   // Save discards edits. Held as the RESOLVED number: a super user opening the dialog should see
   // what Maven is actually running with, not an empty box that happens to mean 5.
   showTopN: number = null;
   maxQueryRangeDays: number = null;

   // CR-20260921-003 (owner rulings 2026-09-21): "Max Results for the Expiration" - how many
   // result cards ONE expiration may contribute. A BACKEND cut (the ranker keeps at most this
   // many of a day's hedge pairs), unlike Show top N, which only limits the display.
   maxResultsPerExpiration: number = null;

   // CR-20260915-002: "Weekdays to exclude" - Maven's OWN starting weekday-exclusion set,
   // independent of the APG ORION's. Default NONE: the stakeholders ruled Maven must not exclude
   // Monday (2026-09-15). Same five-box group as the ORION dialog; an empty set is allowed.
   weekdayExclusions: WeekdayExclusionBox[] = weekdayExclusionBoxes([]);

   @DetectMethodChanges()
   onShown(): void {
      this.showTopN = this._orionSettingsService.mavenShowTopN;
      this.maxQueryRangeDays = this._orionSettingsService.mavenMaxQueryRangeDays;
      this.maxResultsPerExpiration = this._orionSettingsService.mavenMaxResultsPerExpiration;
      this.weekdayExclusions = weekdayExclusionBoxes(this._orionSettingsService.mavenWeekdayExclusions);
   }

   @DetectMethodChanges()
   onWeekdayChanged(box: WeekdayExclusionBox, e: { value?: boolean }): void {
      box.checked = !!(e && e.value);
   }

   @DetectMethodChanges()
   onHidden(): void {
      this.isVisible = false;
   }

   // No floor, no ceiling (same rule as the ORION caps, owner ruling 2026-09-08). The only refusal
   // is an EMPTY box: an empty setting has no meaning, and storing it would make every Maven run
   // fall back to a default the super user never chose.
   async onSaveClicked(): Promise<void> {
      if (!this.isWholeNumber(this.showTopN)) {
         this._toastr.error('"Show top N results" must be a whole number', 'MAVEN');
         return;
      }
      if (!this.isWholeNumber(this.maxQueryRangeDays)) {
         this._toastr.error('"Max Query Range (days)" must be a whole number', 'MAVEN');
         return;
      }
      if (!this.isWholeNumber(this.maxResultsPerExpiration)) {
         this._toastr.error('"Max Results for the Expiration" must be a whole number', 'MAVEN');
         return;
      }

      // Every other field is carried through AS HELD: this dialog never edits them.
      const settings: OrionSettingsDto = {
         ...this._orionSettingsService.get(),
         mavenShowTopN: this.showTopN,
         mavenMaxQueryRangeDays: this.maxQueryRangeDays,
         mavenMaxResultsPerExpiration: this.maxResultsPerExpiration,
         // The checked boxes, possibly NONE: an empty array is stored as the decision it is.
         mavenWeekdayExclusions: this.weekdayExclusions.filter(b => b.checked).map(b => b.id)
      };

      try {
         this.isLoading = true;
         await this._orionSettingsService.save(settings);
         this._toastr.success('MAVEN settings saved');
      } catch (e) {
         this._toastr.error('"Save MAVEN Settings" operation completed with errors');
      } finally {
         this.isLoading = false;
      }
   }

   @DetectMethodChanges()
   onCloseClicked(): void {
      this.onHidden();
   }

   private isWholeNumber(v: any): boolean {
      return typeof v === 'number' && isFinite(v) && Math.trunc(v) === v;
   }
}
